import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {RouterLink} from '@angular/router';
import {Subscription} from 'rxjs';
import {CouponService} from '../../../services/coupon.service';
import {CouponBenefits, CouponRedemption, PendingCouponCode} from '../../../models/coupon';
import {ToastService} from '../../../shared/toast/toast.service';
import {CouponActivationComponent} from './coupon-activation.component';

type LoadState = 'loading' | 'error' | 'ready';

/**
 * SCRUM-312 + 328 - "ההטבות שלי" (/personal/benefits; the backend's coupon
 * notifications link here). Activation form on top, then a summary and one
 * card per coupon in the order the server returns (active first, then
 * history). Expired / cancelled coupons stay visible, greyed. Data from
 * GET client-api/coupons/my/ only.
 */
@Component({
  selector: 'app-benefits',
  standalone: true,
  imports: [RouterLink, CouponActivationComponent],
  templateUrl: './benefits.component.html',
  styleUrls: ['./benefits.component.scss'],
})
export class BenefitsComponent implements OnInit, OnDestroy {
  state: LoadState = 'loading';
  benefits: CouponBenefits | null = null;

  /** coupon_id currently being activated from the "waiting" list */
  activatingId: number | null = null;

  private sub?: Subscription;
  private readonly isBrowser: boolean;

  constructor(private coupons: CouponService, private toast: ToastService,
              @Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // A redemption on this page pushes the fresh payload here at once.
    this.sub = this.coupons.benefits$.subscribe(b => {
      if (b) {
        this.benefits = b;
        this.state = 'ready';
      }
    });
    this.load();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  load(): void {
    if (!this.isBrowser) return;   // personal area is behind AuthGuard; no SSR data
    this.state = 'loading';
    this.coupons.loadMyBenefits().subscribe({
      error: () => { this.state = 'error'; },
    });
  }

  /** Codes meant for this user that were not activated yet. */
  get pending(): PendingCouponCode[] {
    return this.benefits?.pending_codes ?? [];
  }

  get redemptionEnabled(): boolean {
    return this.benefits?.features?.redemption !== false;
  }

  /** One-tap activation of a waiting code (same redeem API as the form). */
  activatePending(p: PendingCouponCode): void {
    if (this.activatingId !== null || !p.can_activate) return;
    this.activatingId = p.coupon_id;
    this.coupons.redeem(p.code).subscribe({
      next: res => {
        this.activatingId = null;
        if (res.status === 201) {
          this.toast.success(res.body?.message || 'הקופון הופעל בהצלחה!',
            {message: (res.body?.details || []).join(' · '), duration: 6000});
        }
      },
      error: err => {
        this.activatingId = null;
        this.toast.error(this.coupons.errorMessage(err, 'לא הצלחנו להפעיל את הקוד. נסו שוב.'));
        this.load();
      },
    });
  }

  get items(): CouponRedemption[] {
    return this.benefits?.coupons ?? [];
  }

  get publishingEnabled(): boolean {
    return this.benefits?.features?.publishing !== false;
  }

  /** ISO date -> DD/MM/YYYY in Israel time ('' when missing / invalid). */
  displayDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Asia/Jerusalem', day: '2-digit', month: '2-digit', year: 'numeric',
      }).format(d);
    } catch (e) {
      const p = (n: number) => String(n).padStart(2, '0');
      return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
    }
  }

  get availableAds(): number {
    return Number(this.benefits?.summary?.available_ads ?? 0);
  }

  isActive(c: CouponRedemption): boolean {
    return c.status === 'ACTIVE';
  }

  /** Expired / cancelled cards are history (greyed). Used-up is shown normally but not "active". */
  isHistory(c: CouponRedemption): boolean {
    return c.status === 'EXPIRED' || c.status === 'CANCELLED';
  }

  progress(c: CouponRedemption): number {
    const p = Number(c.progress_percent);
    return Number.isFinite(p) ? Math.max(0, Math.min(100, p)) : 0;
  }

  statusClass(c: CouponRedemption): string {
    switch (c.status) {
      case 'ACTIVE': return 'bnf-status--active';
      case 'USED_UP': return 'bnf-status--used';
      case 'CANCELLED': return 'bnf-status--cancelled';
      default: return 'bnf-status--expired';
    }
  }

  levelClass(c: CouponRedemption): string {
    return 'bnf-level--' + String(c.ad_level || '').toLowerCase();
  }

  trackById(_: number, c: CouponRedemption): number {
    return c.redemption_id;
  }
}
